import {
  Button,
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
} from '@lightspeed/design-system-react'
import { T, useT } from '@transifex/react'
import { AddressDetails } from '@vend/business-components/dist/components/AddressForm/types'
import { AddressTypes } from '@vend/business-components/dist/components/EditCustomerModal/DetailsTab/Address'
import React, { useEffect, useState } from 'react'
import {
  FulfilmentDetail,
  FulfilmentType,
} from '../../../../../models/fulfilment'
import { useRetailerSettingsManager } from '../../../../../ng-compat/hooks'
import { Address } from './Address'
import {
  AddOrEditAddressButtonType,
  EditAddressButtons,
} from './EditAddressButton'
import {
  AddressFieldValidationResult,
  cityValidation,
  countryValidation,
  stateValidation,
  streetAddress1Validation,
  streetAddress2Validation,
  zipcodeValidation,
} from './address-validation'

interface SaleAddressModalProps {
  formType: AddOrEditAddressButtonType
  fulfillmentDetails?: FulfilmentDetail
  onClose: (fulfillmentDetails?: FulfilmentDetail) => void
  onSave?: (fulfillmentDetails: FulfilmentDetail[]) => void
}

const EMPTY_ADDRESS: AddressDetails = {
  address_1: '',
  address_2: '',
  suburb: '',
  city: '',
  state: '',
  country_id: '',
  postcode: '',
}

export const areAddressesSame = (
  shippingAddress?: AddressDetails,
  billingAddress?: AddressDetails
) => {
  if (!billingAddress) return true
  if (!shippingAddress) return !billingAddress

  return !(Object.keys(shippingAddress) as Array<keyof AddressDetails>).some(
    key => billingAddress[key] !== shippingAddress[key]
  )
}

export const SaleAddressModal: React.FC<SaleAddressModalProps> = ({
  formType,
  fulfillmentDetails,
  onClose,
  onSave,
}: SaleAddressModalProps) => {
  const t = useT()
  const [isFormSubmit, setIsFormSubmit] = useState(() => false)
  const [isFormValid, setIsFormValid] = useState(() => true)
  const [hasSameBillingAddress, setHasSameBillingAddress] = useState(
    areAddressesSame(
      fulfillmentDetails?.shippingAddress,
      fulfillmentDetails?.billingAddress
    )
  )

  const retailer = useRetailerSettingsManager().get()

  const [billingAddress, setBillingAddress] = useState(() =>
    formType === EditAddressButtons.add || !fulfillmentDetails?.billingAddress
      ? { ...EMPTY_ADDRESS, country_id: retailer?.country || '' }
      : fulfillmentDetails?.billingAddress
  )
  const [shippingAddress, setShippingAddress] = useState(() =>
    formType === EditAddressButtons.add ||
    formType === EditAddressButtons.addShipping ||
    !fulfillmentDetails?.shippingAddress
      ? { ...EMPTY_ADDRESS, country_id: retailer?.country || '' }
      : fulfillmentDetails?.shippingAddress
  )
  const [billingAddressErrors, setBillingAddressErrors] = useState(
    () => EMPTY_ADDRESS
  )
  const [shippingAddressErrors, setShippingAddressErrors] = useState(
    () => EMPTY_ADDRESS
  )

  // Run validation on initial load when editing existing address
  useEffect(() => {
    if (formType === EditAddressButtons.edit) {
      doFormValidation(shippingAddress, billingAddress)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Handle escape key to prevent propagation to parent modal
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation()
        onClose(fulfillmentDetails)
      }
    }

    document.addEventListener('keydown', handleEscapeKey, true)
    return () => {
      document.removeEventListener('keydown', handleEscapeKey, true)
    }
  }, [onClose, fulfillmentDetails])

  const handleClearForm = () => {
    setShippingAddress(EMPTY_ADDRESS)
    setShippingAddressErrors(EMPTY_ADDRESS)
    setBillingAddress(EMPTY_ADDRESS)
    setBillingAddressErrors(EMPTY_ADDRESS)
    setIsFormValid(true)
    setIsFormSubmit(false)
  }

  const isFieldEmpty = (value: string) => !value || value === ''

  const isAddressEmpty =
    (!shippingAddress ||
      Object.values(shippingAddress).every(field => isFieldEmpty(field))) &&
    (!billingAddress ||
      Object.values(billingAddress).every(field => isFieldEmpty(field)))

  const getValidationErrorMessage = (
    validationResult: AddressFieldValidationResult
  ): string => {
    return !validationResult.isValid
      ? t(validationResult.errorMessage, {
          _context: validationResult._context,
        })
      : ''
  }
  const validateAddress = (address: AddressDetails): AddressDetails => {
    return {
      address_1: getValidationErrorMessage(
        streetAddress1Validation(address.address_1)
      ),
      address_2: getValidationErrorMessage(
        streetAddress2Validation(address.address_2)
      ),
      suburb: '',
      city: getValidationErrorMessage(cityValidation(address.city)),
      state: getValidationErrorMessage(
        stateValidation(address.state, address.country_id)
      ),
      country_id: getValidationErrorMessage(
        countryValidation(address.country_id)
      ),
      postcode: getValidationErrorMessage(
        zipcodeValidation(address.postcode, address.country_id)
      ),
    }
  }

  const doFormValidation = (
    currentShippingAddress = shippingAddress,
    currentBillingAddress = billingAddress,
    currentHasSameBillingAddress = hasSameBillingAddress
  ) => {
    const shippingTempAddressError = validateAddress(currentShippingAddress)
    const hasShippingAddressError = Object.values(
      shippingTempAddressError
    ).some(value => !isFieldEmpty(value))
    setShippingAddressErrors(
      hasShippingAddressError ? shippingTempAddressError : EMPTY_ADDRESS
    )

    let hasBillingAddressError = false
    if (!currentHasSameBillingAddress) {
      const billingTempAddressError = validateAddress(currentBillingAddress)
      hasBillingAddressError = Object.values(billingTempAddressError).some(
        value => !isFieldEmpty(value)
      )
      setBillingAddressErrors(
        hasBillingAddressError ? billingTempAddressError : EMPTY_ADDRESS
      )
    } else {
      setBillingAddressErrors(EMPTY_ADDRESS)
    }

    const isValid = !hasShippingAddressError && !hasBillingAddressError
    setIsFormValid(isValid)
    return isValid
  }

  const saveForm = () => {
    setIsFormSubmit(true)
    if (!doFormValidation()) return

    const newFulfillmentDetail = [
      {
        type: fulfillmentDetails?.type || FulfilmentType.DELIVERY,
        note: fulfillmentDetails?.note || '',
        billingAddress,
        shippingAddress,
        shouldAddNewAddress: false,
      },
    ]

    if (onSave) onSave(newFulfillmentDetail)
    onClose(newFulfillmentDetail[0])
  }

  return (
    <div className="sale-address-modal-wrapper">
      <Modal
        onClose={() => onClose(fulfillmentDetails)}
        size="large"
        className="customer-dialog"
      >
        <ModalHeader>
          <T _str="Edit the address for this sale" _context="Modal title" />
        </ModalHeader>
        <ModalContent scrollable>
          <p className="vd-mb6">
            <T
              _str="The address in the customer profile will remain unchanged."
              _context="Modal description"
            />
          </p>
          <Address
            key="shipping-address"
            type={AddressTypes.Shipping}
            addressInfo={shippingAddress}
            errors={shippingAddressErrors}
            hasSameBillingAddress={hasSameBillingAddress}
            setHasSameBillingAddress={value => {
              setHasSameBillingAddress(value)
              if (value) {
                setBillingAddress(shippingAddress)
                doFormValidation(shippingAddress, shippingAddress, value)
              }
            }}
            onChange={value => {
              setShippingAddress(value)
              if (hasSameBillingAddress) setBillingAddress(value)
              if (isFormSubmit || !isFormValid) {
                const newBillingAddress = hasSameBillingAddress
                  ? value
                  : billingAddress
                doFormValidation(value, newBillingAddress)
              }
            }}
          />
          {!hasSameBillingAddress && (
            <Address
              key="billing-address"
              type={AddressTypes.Billing}
              addressInfo={billingAddress}
              errors={billingAddressErrors}
              onChange={value => {
                setBillingAddress(value)
                if (isFormSubmit || !isFormValid) {
                  doFormValidation(shippingAddress, value)
                }
              }}
            />
          )}
        </ModalContent>
        <ModalActions>
          <>
            {!isAddressEmpty && (
              <Button
                className="vd-mr2"
                data-track="edit-sale-address-modal-clear"
                modifier="text"
                id="edit-sale-address-modal-clear"
                key="formClear"
                onClick={handleClearForm}
                form="vd-edit-customer-form"
                variant="supplementary"
              >
                <T _str="Clear all fields" _context="Button text" />
              </Button>
            )}
            <Button
              data-track="edit-sale-address-modal-submit"
              id="edit-sale-address-modal-submit"
              key="formSubmit"
              type="submit"
              form="vd-edit-customer-form"
              disabled={!isFormValid}
              onClick={saveForm}
            >
              <T _str="Save Changes" _context="Button text" />
            </Button>
          </>
        </ModalActions>
      </Modal>
    </div>
  )
}
